import type { Embed } from '@pen/shared';

const scripts = new Map<string, Promise<void>>();
const styles = new Map<string, Promise<void>>();
function asset(url: string, type: 'script' | 'style'): Promise<void> {
  const cache = type === 'script' ? scripts : styles;
  const existing = cache.get(url);
  if (existing) return existing;
  const promise = new Promise<void>((resolve, reject) => {
    const element = type === 'script' ? document.createElement('script') : document.createElement('link');
    if (element instanceof HTMLScriptElement) { element.src = url; element.async = false; }
    else { element.rel = 'stylesheet'; element.href = url; }
    element.onload = () => {
      if (type === 'script' && /\.voice\.js(?:\?|$)/.test(url)) {
        // Authored voice manifests are relative to their course page, not the app's /lesson/ route.
        const path = new URL(url, location.origin).pathname;
        const base = new URL(path.slice(0, path.lastIndexOf('/assets/') + 1), location.origin);
        // XP is the trusted course kit's browser global; the package has no TypeScript declarations.
        const globals = window as unknown as { XP?: { VOICE?: Record<string, { lines?: Record<string, { src?: string }> }> } };
        const xp = globals.XP;
        for (const manifest of Object.values(xp?.VOICE ?? {})) {
          for (const line of Object.values(manifest.lines ?? {})) {
            if (line.src && !/^(?:[a-z]+:|\/)/i.test(line.src)) line.src = new URL(line.src, base).href;
          }
        }
      }
      resolve();
    };
    element.onerror = () => { cache.delete(url); element.remove(); reject(new Error(`Could not load ${url}`)); };
    document.head.append(element);
  });
  cache.set(url, promise);
  return promise;
}
export interface ActProgress { act: number; ended: boolean }
export interface LoadedEmbed { onAct(callback: (progress: ActProgress) => void): () => void; destroy(): void }

export function observeActs(root: ParentNode, acts: string[] | null, onAct: (progress: ActProgress) => void): () => void {
  if (acts?.length === 0) return () => {};
  let previous = '';
  const timer = window.setInterval(() => {
    const scrub = root.querySelector('.xp-scrub[aria-valuetext]');
    if (!scrub) return;
    const card = scrub.closest('.xp-card');
    if (card?.classList.contains('poster') || card?.classList.contains('calm')) return;
    const text = scrub.getAttribute('aria-valuetext') ?? '';
    const title = text.includes(', ') ? text.slice(text.indexOf(', ') + 2) : text;
    const act = acts ? acts.findIndex(value => value.trim().toLocaleLowerCase() === title.trim().toLocaleLowerCase())
      : Array.from(scrub.querySelectorAll('.segs i')).findIndex(segment => segment.classList.contains('cur'));
    if (act < 0) return;
    const now = Number(scrub.getAttribute('aria-valuenow'));
    const max = Number(scrub.getAttribute('aria-valuemax'));
    const play = card?.querySelector('.b-play');
    const ended = max > 0 && now >= max && play?.getAttribute('aria-label') === 'Play';
    const signature = `${act}:${ended}`;
    if (signature === previous) return;
    previous = signature;
    onAct({ act, ended });
  }, 200);
  return () => clearInterval(timer);
}

export function pauseEmbeds(root: ParentNode): void {
  root.querySelectorAll<HTMLVideoElement>('video').forEach(video => video.pause());
  root.querySelectorAll<HTMLButtonElement>('.b-play[aria-label="Pause"]').forEach(button => button.click());
  for (const frame of root.querySelectorAll<HTMLIFrameElement>('iframe')) {
    if (frame.contentDocument) pauseEmbeds(frame.contentDocument);
  }
}

export async function loadEmbed(host: HTMLElement, embed: Embed): Promise<LoadedEmbed> {
  for (const url of embed.styles) await asset(url, 'style');
  for (const url of embed.scripts) await asset(url, 'script');
  const parts = embed.call.split('.');
  let owner: unknown = window;
  for (const part of parts.slice(0, -1)) {
    if (!owner || (typeof owner !== 'object' && typeof owner !== 'function')) throw new Error(`The course embed ${embed.call} is unavailable.`);
    owner = Reflect.get(owner, part);
  }
  const callable = owner && (typeof owner === 'object' || typeof owner === 'function') ? Reflect.get(owner, parts.at(-1)!) : undefined;
  if (typeof callable !== 'function') throw new Error(`The course embed ${embed.call} is unavailable.`);
  const mounted: unknown = callable.call(owner, host, embed.options);
  const callbacks = new Set<(progress: ActProgress) => void>();
  let latest: ActProgress | null = null;
  const stopObserving = observeActs(host, embed.acts, progress => {
    latest = progress;
    for (const callback of callbacks) callback(progress);
  });
  return {
    onAct(callback) { callbacks.add(callback); if (latest) callback(latest); return () => { callbacks.delete(callback); }; },
    destroy() {
      stopObserving(); callbacks.clear();
      pauseEmbeds(host);
      if (typeof mounted === 'function') mounted();
      else if (mounted && typeof mounted === 'object' && 'destroy' in mounted && typeof mounted.destroy === 'function') mounted.destroy();
      host.replaceChildren();
    },
  };
}
